"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Vehicle, VehicleType } from "@/types";
import {
  vehicleService,
  CreateVehiclePayload,
} from "@/services/vehicle/vehicleService";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";
import { useCustomers } from "./CustomerContext";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

interface VehicleContextType {
  vehicles: Vehicle[];
  loading: boolean;
  error: string | null;
  addVehicle: (
    vehicle: Omit<Vehicle, "id" | "totalServices">,
  ) => Promise<Vehicle>;
  updateVehicle: (id: string, vehicle: Partial<Vehicle>) => void;
  deleteVehicle: (id: string) => void;
  getVehicleById: (id: string) => Vehicle | undefined;
  getVehiclesByCustomerId: (customerId: string) => Vehicle[];
  fetchVehicles: () => Promise<void>;
  refreshVehicles: () => Promise<void>;
}

const VehicleContext = createContext<VehicleContextType | undefined>(undefined);

// Helper to map vehicle type from API format to frontend format
function mapApiTypeToFrontend(apiType: string): VehicleType {
  const typeLower = (apiType || "").toLowerCase();
  if (typeLower === "sedan") return "sedan";
  if (typeLower === "suv") return "suv";
  if (typeLower === "truck") return "truck";
  if (typeLower === "van") return "van";
  return "sedan"; // default
}

// Helper to extract name from make/model (handles both string and object)
function extractName(value: any): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.name) return value.name;
  return "";
}

export function VehicleProvider({ children }: { children: ReactNode }) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { customers } = useCustomers(); // For validation if needed
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchVehicles = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await vehicleService.getAllVehicles();
      // Vehicles API returns userId, which corresponds to customer's id (user id)
      // The customer's id in the frontend is the user id from the API
      const transformed = data.map((v: any) => ({
        id: v.id,
        // Map userId to customerId - customer's id is their user id
        customerId:
          v.userId || v.user_id || v.customerId || v.customer_id || "",
        plateNumber: v.plateNumber || v.plate_number || "",
        make: extractName(v.Make) || extractName(v.make) || v.makeName || "",
        model:
          extractName(v.Model) || extractName(v.model) || v.modelName || "",
        color: v.color || "",
        type: mapApiTypeToFrontend(v.type || v.vehicleType || "sedan"),
        totalServices: v.totalServices || v.total_services || 0,
        lastService: v.lastService ? new Date(v.lastService) : undefined,
        year: v.year || null,
        notes: v.notes || null,
      }));
      setVehicles(transformed);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch vehicles";
      setError(errorMessage);
      console.error("Error fetching vehicles:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshVehicles = useCallback(async () => {
    await fetchVehicles();
  }, [fetchVehicles]);

  const addVehicle = useCallback(
    async (vehicleData: Omit<Vehicle, "id" | "totalServices">) => {
      try {
        // If make/model are provided as strings (from appointment form), we need to find or create them
        let makeId = "";
        let modelId = "";

        // Check if make/model are provided as strings (appointment form) or IDs (vehicle management)
        if (
          "make" in vehicleData &&
          typeof vehicleData.make === "string" &&
          vehicleData.make
        ) {
          const makeName = vehicleData.make;
          // Find or create make
          try {
            const makes = await makeService.getAllMakes();
            let make = makes.find(
              (m: any) => m.name?.toLowerCase() === makeName.toLowerCase(),
            );
            if (!make) {
              // Create new make
              make = await makeService.createMake({ name: makeName });
            }
            makeId = make.id;
          } catch (error) {
            console.error("Error handling make:", error);
            toast.error("Failed to process vehicle make");
            throw error;
          }

          // Find or create model
          if (
            "model" in vehicleData &&
            typeof vehicleData.model === "string" &&
            vehicleData.model
          ) {
            const modelName = vehicleData.model;
            try {
              const models = await modelService.getAllModels();
              let model = models.find(
                (m: any) =>
                  m.name?.toLowerCase() === modelName.toLowerCase() &&
                  m.makeId === makeId,
              );
              if (!model) {
                // Create new model
                model = await modelService.createModel({
                  name: modelName,
                  makeId,
                });
              }
              modelId = model.id;
            } catch (error) {
              console.error("Error handling model:", error);
              toast.error("Failed to process vehicle model");
              throw error;
            }
          }
        } else {
          // Assume makeId and modelId are provided (from vehicle management form)
          makeId = (vehicleData as any).makeId || "";
          modelId = (vehicleData as any).modelId || "";
        }

        // Map vehicle type to API format
        // Map vehicle type to API format
        const typeMap: Record<string, string> = {
          sedan: "Sedan",
          suv: "SUV",
          truck: "Truck",
          van: "Van",
        };

        const payload: CreateVehiclePayload = {
          type: vehicleData.type
            ? typeMap[vehicleData.type] || "Sedan"
            : undefined,
          plateNumber: vehicleData.plateNumber,
          color: vehicleData.color,
          modelId: modelId || undefined,
          makeId: makeId || undefined,
          customerId: vehicleData.customerId,
          year: vehicleData.year,
        };

        // Create vehicle via API
        const apiVehicle = await vehicleService.createVehicle(payload);

        // Transform API response to frontend format
        const newVehicle: Vehicle = {
          id: apiVehicle.id,
          customerId:
            apiVehicle.userId ||
            apiVehicle.customerId ||
            vehicleData.customerId,
          plateNumber: apiVehicle.plateNumber || vehicleData.plateNumber,
          make: vehicleData.make || apiVehicle.Make?.name || "",
          model: vehicleData.model || apiVehicle.Model?.name || "",
          color: apiVehicle.color || vehicleData.color,
          type: vehicleData.type,
          totalServices: 0,
          lastService: undefined,
          year: apiVehicle.year || vehicleData.year,
          notes: apiVehicle.notes || vehicleData.notes,
        };

        // Optimistically add to state
        setVehicles((prev) => [newVehicle, ...prev]);

        // Refresh vehicles to get latest data
        await fetchVehicles();

        toast.success("Vehicle created successfully");
        return newVehicle;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create vehicle";
        toast.error(errorMessage);
        throw error;
      }
    },
    [fetchVehicles],
  );

  const updateVehicle = useCallback(
    (id: string, vehicleData: Partial<Vehicle>) => {
      setVehicles((prev) =>
        prev.map((v) => (v.id === id ? { ...v, ...vehicleData } : v)),
      );
    },
    [],
  );

  const deleteVehicle = useCallback((id: string) => {
    setVehicles((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const getVehicleById = useCallback(
    (id: string) => {
      return vehicles.find((v) => v.id === id);
    },
    [vehicles],
  );

  const getVehiclesByCustomerId = useCallback(
    (customerId: string) => {
      return vehicles.filter((v) => v.customerId === customerId);
    },
    [vehicles],
  );

  // Fetch vehicles on mount, but only if authenticated
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated) {
      fetchVehicles();
    }
  }, [fetchVehicles, authLoading, isAuthenticated]);

  const value: VehicleContextType = {
    vehicles,
    loading,
    error,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    getVehicleById,
    getVehiclesByCustomerId,
    fetchVehicles,
    refreshVehicles,
  };

  return (
    <VehicleContext.Provider value={value}>{children}</VehicleContext.Provider>
  );
}

export function useVehicles() {
  const context = useContext(VehicleContext);
  if (context === undefined) {
    throw new Error("useVehicles must be used within a VehicleProvider");
  }
  return context;
}
